import React, { useMemo, useState } from 'react'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import MaldivesDirectSearchFlightCalendarMobile from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import CharterBundleCalendarActions from './CharterBundleCalendarActions'
import { useOfferCharterCalendarData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useOfferCharterCalendarData'
import { filterDateRangesByNights } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import { parseAsBoolean, useQueryState } from 'nuqs'
import { OfferDurationOption } from '../DurationTabs/OfferDurationTabs'
import { OfferPagePriceMode } from 'contexts/OfferPage/offerPageStateReducer'
import { FlightsClassTypes } from 'constants/flight'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import moment from 'moment'

export interface CharterTripOption {
  checkIn: string
  checkOut: string
  departDate: string
  returnDate: string
}

interface Props {
  offer: App.HotelOffer
  occupancies: Array<App.Occupants>
  duration: number
  durationOptions: Array<OfferDurationOption>
  priceMode: OfferPagePriceMode
  fareClass: FlightsClassTypes
  multipleRoomBookingActive: boolean
  charterBundleRoute: App.CharterBundleRoute
  checkInDate?: string
  onDurationChange: (nights: number) => void
  onClose?: () => void
  onBack?: () => void
  onDateSelected: (option: CharterTripOption) => void
  onContinue?: () => void
  onResetDates?: () => void
  onFareClassChange: (fareClass: FlightsClassTypes) => void
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>
  disableContinue?: boolean
}

function CharterCalendarFilter(props: Props) {
  const {
    offer,
    occupancies,
    duration,
    durationOptions,
    priceMode,
    fareClass,
    multipleRoomBookingActive,
    charterBundleRoute,
    checkInDate,
    onDurationChange,
    onClose,
    onBack,
    onDateSelected,
    onContinue,
    onResetDates,
    onFareClassChange,
    scrollContainerRef,
    disableContinue,
  } = props

  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState('luxPlus', parseAsBoolean.withDefault(true))
  const [selectedMonth, setSelectedMonth] = useState(checkInDate ? moment(checkInDate).format('MMM') : moment().format('MMM'))

  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies,
    fareClass,
    disabled: occupancies.length === 0,
  })

  const dateRangesByMonth = useMemo(
    () => filterDateRangesByNights(allDateRangesByMonth, duration),
    [allDateRangesByMonth, duration]
  )

  const flightCalendarData = useOfferCharterCalendarData({
    offer,
    nights: duration,
    rooms: occupancies,
    fareClass,
    dateRangesByMonth,
    multipleRoomBookingActive,
    charterBundleRoute,
  })

  const selectedOptionId = useMemo(() => checkInDate ? `${checkInDate}-${duration}n` : undefined, [checkInDate, duration])

  const durationDropdownOptions = useMemo(() => {
    // Get valid durations for charter flights (e.g., 5 and 12 nights for Maldives)
    const validCharterDurations = CHARTER_FLIGHT_DETAILS[charterBundleRoute]?.validDurations ?? []
    // Only include duration options that are valid charter durations and have available (non-sold-out) packages
    return durationOptions
      .filter(option => !option.soldOut && validCharterDurations.includes(option.duration))
      .map(option => ({
        value: option.duration,
        label: `${option.duration} nights & flights`,
      }))
  }, [durationOptions, charterBundleRoute])

  const actionsElement = (
    <CharterBundleCalendarActions
      nights={duration}
      onDurationChange={onDurationChange}
      durationOptions={durationDropdownOptions}
      luxPlusEnabled={luxPlusEnabled}
      onLuxPlusToggle={setLuxPlusEnabled}
      fareClass={fareClass}
      onFareClassChange={onFareClassChange}
    />
  )

  const handleDateSelect = (option: CharterTripOption) => {
    onDateSelected(option)
    setSelectedMonth(moment(option.checkIn).format('MMM'))
  }

  return (
    <>
      <ModalHeader
        title="Select your trip dates"
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <Group direction="vertical" gap={12}>
          <BodyText variant="medium" colour="neutral-two">
            Flights depart Sunday night and arrive on Monday for hotel check-in.
          </BodyText>
          <HorizontalContainerWithFadedOverflow overflowGutter={20} gap={8}>
            {actionsElement}
          </HorizontalContainerWithFadedOverflow>
        </Group>
      </ModalHeader>
      <ModalBody ref={scrollContainerRef}>
        <ModalContent>
          <MaldivesDirectSearchFlightCalendarMobile
            onDateSelect={handleDateSelect}
            nights={duration}
            occupancies={occupancies}
            luxPlusEnabled={luxPlusEnabled}
            itemBundleDataByDate={flightCalendarData.itemBundleDataByDate}
            dateRangesByMonth={dateRangesByMonth}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            fareClass={fareClass}
            priceMode={priceMode}
            selectedOptionId={selectedOptionId}
            mode="offer"
            scrollContainerRef={scrollContainerRef}
          />
        </ModalContent>
      </ModalBody>
      <ModalFooter
        secondaryActionProps={onResetDates ? getSecondaryActionProps(onResetDates) : undefined}
        primaryActionProps={onContinue ? getPrimaryActionProps(onContinue, disableContinue) : undefined}
      />
    </>
  )
}

function getSecondaryActionProps(onResetDates?: () => void) {
  if (!onResetDates) return undefined

  return {
    children: 'Reset',
    onClick: onResetDates,
    type: 'button' as const,
  }
}

function getPrimaryActionProps(onContinue?: () => void, disabled?: boolean) {
  if (!onContinue) return undefined

  return {
    children: 'Continue',
    onClick: onContinue,
    type: 'button' as const,
    disabled,
  }
}

export default CharterCalendarFilter
